import React, { useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  InputLabel,
  ListItem,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GroupIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person';
import RemoveIcon from '@mui/icons-material/Remove';
import { useNavigate } from 'react-router-dom';
import {
  ILegalTransactionDataCollection,
  ILegalTransactionFullObject,
} from '../Interfaces/ILegalTransaction';
import { customFetcher } from '../hooks/useFetch';
import {
  useGetLegalTransactionProgresses,
  useGetLegalTransactionStates,
} from '../hooks/reactQuerys/LegalTransactionQueries';
import {
  useGetUserGroups,
  useGetUsers,
} from '../hooks/reactQuerys/UserQueries';
import { useAlert } from '../context/AlertContext';
import { selectableUserGroups } from '../Interfaces/IUserGroup';

interface IProps {
  legalTransaction: ILegalTransactionFullObject;
}

type SectionKey =
  | 'realEstate'
  | 'dataCollection'
  | 'clients'
  | 'drafts'
  | 'publicDocs'
  | 'internalDocs';

interface ISectionItem {
  /** Eindeutige Item-ID innerhalb der Section. */
  id: string;
  label: string;
  /** Optionaler Untertitel (z. B. Datum, Pfad) — klein darunter dargestellt. */
  hint?: string;
}

interface ISectionDef {
  key: SectionKey;
  title: string;
  items: ISectionItem[];
}

type WorkflowMode = 'reset' | 'keep';

// LegalRequirementState 10 = "Offen" laut DB
const STATE_OFFEN = 10;

/**
 * "Vorgang kopieren"-Dialog mit Tree-Auswahl pro Bereich + editierbaren
 * Header-Feldern (Titel, Urkundennummer, Geschäftsnummer, Beurkundungsdatum,
 * Notar, Mitarbeiter, Status, Phase).
 *
 * Was IMMER ausgeschlossen wird: Rechnungen, Urkunden, Notizen, Aufgaben,
 * Termine, Logs, GwG, SpecialFunction, Talkroom-Drafts.
 *
 * Bei der Abwicklung kann der User wählen, ob alle Maßnahmen / Vollzüge
 * auf "Offen" zurückgesetzt werden oder der aktuelle Bearbeitungsstand
 * 1:1 übernommen wird.
 */
export const LegalTransactionCopy: React.FC<IProps> = ({
  legalTransaction,
}) => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [isPending, setIsPending] = useState(false);
  const { users } = useGetUsers();
  const { userGroups } = useGetUserGroups();
  const { legalTransactionProgresses } = useGetLegalTransactionProgresses();
  const { legalTransactionStates } = useGetLegalTransactionStates();

  const [open, setOpen] = useState(false);

  // Header — vorbefüllt aus dem Original. Nummern als Liste, damit alle
  // Vorgangs-/Urkundennummern angezeigt werden koennen (gleiches Pattern
  // wie in `LegalTransactionEdit`).
  const [title, setTitle] = useState(legalTransaction.Title || '');
  const [transactionNumbers, setTransactionNumbers] = useState<string[]>(() => {
    const arr = (legalTransaction.TransactionNumberArray || [])
      .map(t => t.TransactionNumberEntry || '')
      .filter(s => s.trim());
    return arr.length > 0 ? arr : [''];
  });
  const [recordNumbers, setRecordNumbers] = useState<string[]>(() => {
    const arr = (legalTransaction.RecordNumberArray || [])
      .map(r => r.RecordNumberEntry || '')
      .filter(s => s.trim());
    return arr.length > 0 ? arr : [''];
  });
  const [notarizationDate, setNotarizationDate] = useState(
    legalTransaction.NotarizationDate
      ? String(legalTransaction.NotarizationDate).slice(0, 10)
      : ''
  );
  const [notaryId, setNotaryId] = useState<number | ''>(
    legalTransaction.Notary_idUser ?? ''
  );

  // Mitarbeiter ODER Gruppe — analog zum Autocomplete in LegalTransactionEdit:
  // ``Employee_idUser`` und ``idUserGroup`` schliessen sich gegenseitig aus.
  type AssigneeType = 'user' | 'group' | null;
  const initialAssigneeType: AssigneeType = legalTransaction.idUserGroup
    ? 'group'
    : legalTransaction.Employee_idUser
      ? 'user'
      : null;
  const [assigneeType, setAssigneeType] =
    useState<AssigneeType>(initialAssigneeType);
  const [employeeId, setEmployeeId] = useState<number | null>(
    legalTransaction.Employee_idUser ?? null
  );
  const [groupId, setGroupId] = useState<number | null>(
    legalTransaction.idUserGroup ?? null
  );

  const [stateId, setStateId] = useState<number | ''>(
    legalTransaction.idLegalTransactionState ?? ''
  );
  const [progressId, setProgressId] = useState<number | ''>(
    legalTransaction.idLegalTransactionProgress ?? ''
  );
  const [workflowMode, setWorkflowMode] = useState<WorkflowMode>('reset');

  // Mitarbeiter + Gruppen kombiniert (selbe Struktur wie in LegalTransactionEdit).
  // Wichtig: der bereits zugewiesene Mitarbeiter wird zwingend mit aufgenommen,
  // auch wenn er die normalen Filter (kein Notar, nicht gesperrt) nicht
  // erfuellt — sonst bliebe das Autocomplete-Feld trotz Vorbefuellung leer.
  const combinedAssignees = useMemo(() => {
    const assignedId = legalTransaction.Employee_idUser ?? null;
    const employees = (users || [])
      .filter(
        u =>
          (!u.isNotary && !u.isBlocked) ||
          (assignedId !== null && u.idUser === assignedId)
      )
      .map(u => ({
        type: 'Mitarbeiter' as const,
        id: u.idUser,
        name: `${u.FirstName} ${u.LastName}`,
      }));
    const groups = selectableUserGroups(userGroups).map(g => ({
      type: 'Gruppe' as const,
      id: g.idUserGroup,
      name: g.UserGroup,
    }));
    return [...employees, ...groups];
  }, [users, userGroups, legalTransaction.Employee_idUser]);

  const selectedAssignee = useMemo(() => {
    if (assigneeType === 'user' && employeeId !== null) {
      return (
        combinedAssignees.find(
          o => o.type === 'Mitarbeiter' && o.id === employeeId
        ) || null
      );
    }
    if (assigneeType === 'group' && groupId !== null) {
      return (
        combinedAssignees.find(o => o.type === 'Gruppe' && o.id === groupId) ||
        null
      );
    }
    return null;
  }, [assigneeType, employeeId, groupId, combinedAssignees]);

  // Sub-Item-Listen pro Bereich aus dem Original ermitteln.
  const sections = useMemo<ISectionDef[]>(
    () => buildSections(legalTransaction),
    [legalTransaction]
  );

  // Selektions-State: pro Section ein Set von Item-IDs (initial: alle).
  const [selected, setSelected] = useState<Record<SectionKey, Set<string>>>(
    () => {
      const initial = {} as Record<SectionKey, Set<string>>;
      for (const s of sections) {
        initial[s.key] = new Set(s.items.map(i => i.id));
      }
      return initial;
    }
  );

  const notaries = useMemo(
    () => (users || []).filter(u => u.isNotary),
    [users]
  );

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    if (isPending) return;
    setOpen(false);
  };

  const handleCopy = async () => {
    if (!title.trim()) {
      showAlert({
        severity: 'warning',
        text: 'Bitte einen Titel für den neuen Vorgang angeben.',
      });
      return;
    }
    setIsPending(true);
    try {
      // Selektionen in einfache ID-Listen ueberfuehren (das Backend kennt
      // die Item-IDs als DiNo-PKs, nicht als Section-Item-Strings).
      const selectedClients = (legalTransaction.LegalClientArray || [])
        .filter(c => selected.clients.has(`lc-${c.idLegalClient}`))
        .map(c => c.idLegalClient);
      const selectedDraftDocs = (legalTransaction.DraftArray || [])
        .filter(d => selected.drafts.has(`draft-${d.idDocument}`))
        .map(d => d.idDocument);
      const selectedPublicDocs = (legalTransaction.OtherDocumentArray || [])
        .filter(d => selected.publicDocs.has(`pub-${d.idDocument}`))
        .map(d => d.idDocument);

      // Connexion akzeptiert bei Integer-Feldern kein `null` (Swagger 2.0).
      // Daher null-Werte weglassen statt explizit zu setzen.
      const body: Record<string, unknown> = {
        title: title.trim(),
        transactionNumbers: transactionNumbers
          .map(s => s.trim())
          .filter(s => s.length > 0),
        recordNumbers: recordNumbers
          .map(s => s.trim())
          .filter(s => s.length > 0),
        workflowMode,
        selectedClients,
        selectedDraftDocs,
        selectedPublicDocs,
      };
      if (notarizationDate) body.notarizationDate = notarizationDate;
      if (typeof notaryId === 'number') body.notaryId = notaryId;
      if (assigneeType === 'user' && typeof employeeId === 'number') {
        body.employeeId = employeeId;
      }
      if (assigneeType === 'group' && typeof groupId === 'number') {
        body.groupId = groupId;
      }
      const effStateId =
        typeof stateId === 'number'
          ? stateId
          : legalTransaction.idLegalTransactionState;
      if (typeof effStateId === 'number') body.stateId = effStateId;
      const effProgressId =
        typeof progressId === 'number'
          ? progressId
          : legalTransaction.idLegalTransactionProgress;
      if (typeof effProgressId === 'number') body.progressId = effProgressId;

      const response = (await customFetcher({
        endpoint: `/legaltransaction/${legalTransaction.idLegalTransaction}/copy`,
        method: 'POST',
        body,
      })) as { idLegalTransaction?: number };

      const newId = response?.idLegalTransaction;
      if (!newId || newId <= 0) {
        throw new Error('Anlegen des Vorgangs schlug fehl.');
      }

      showAlert({
        severity: 'success',
        text: 'Vorgang erfolgreich kopiert.',
      });
      setOpen(false);
      navigate(`/legaltransaction/${newId}`);
    } catch (e) {
      showAlert({
        severity: 'error',
        text: 'Vorgang konnte nicht kopiert werden.',
      });
      console.error(e);
    } finally {
      setIsPending(false);
    }
  };

  return (
    <>
      <Button
        variant='outlined'
        startIcon={<ContentCopyIcon />}
        onClick={handleOpen}
      >
        Kopieren
      </Button>

      {open && (
        <Dialog open onClose={handleClose} maxWidth='md' fullWidth>
          <DialogTitle>Vorgang kopieren</DialogTitle>
          <DialogContent>
            <Alert severity='info' sx={{ mt: 1, mb: 2 }}>
              Der neue Vorgang übernimmt nur die ausgewählten Inhalte.
              Rechnungen, Urkunden, Notizen, Aufgaben und Termine werden
              grundsätzlich nicht kopiert.
            </Alert>

            <Typography variant='subtitle2' sx={{ mt: 1, mb: 1 }}>
              Kopf des Vorgangs (des neuen Vorgangs)
            </Typography>
            <Stack spacing={2} sx={{ mb: 2 }}>
              <TextField
                label='Titel'
                value={title}
                onChange={e => setTitle(e.target.value)}
                fullWidth
                required
              />
              <Stack direction='row' spacing={2} alignItems='flex-start'>
                <Box sx={{ flex: 1 }}>
                  <NumberListField
                    label='Vorgangsnummer'
                    values={transactionNumbers}
                    onChange={setTransactionNumbers}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <NumberListField
                    label='Urkundennummer'
                    values={recordNumbers}
                    onChange={setRecordNumbers}
                  />
                </Box>
              </Stack>
              <Stack direction='row' spacing={2}>
                <TextField
                  label='Beurkundungsdatum'
                  type='date'
                  value={notarizationDate}
                  onChange={e => setNotarizationDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Stack>
              <Stack direction='row' spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>Notar</InputLabel>
                  <Select
                    value={notaryId}
                    onChange={e => setNotaryId(Number(e.target.value) || '')}
                    label='Notar'
                  >
                    <MenuItem value=''>(nicht zugewiesen)</MenuItem>
                    {notaries.map(u => (
                      <MenuItem key={u.idUser} value={u.idUser}>
                        {u.FirstName} {u.LastName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Autocomplete
                  fullWidth
                  options={combinedAssignees}
                  groupBy={option => option.type}
                  getOptionLabel={option => option.name}
                  isOptionEqualToValue={(o, v) =>
                    o.type === v.type && o.id === v.id
                  }
                  value={selectedAssignee}
                  onChange={(_, val) => {
                    if (!val) {
                      setAssigneeType(null);
                      setEmployeeId(null);
                      setGroupId(null);
                      return;
                    }
                    if (val.type === 'Mitarbeiter') {
                      setAssigneeType('user');
                      setEmployeeId(val.id);
                      setGroupId(null);
                    } else {
                      setAssigneeType('group');
                      setGroupId(val.id);
                      setEmployeeId(null);
                    }
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label={
                        assigneeType === 'group'
                          ? 'Zugewiesene Gruppe'
                          : 'Zugewiesener Mitarbeiter'
                      }
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            {assigneeType === 'group' ? (
                              <GroupIcon
                                fontSize='small'
                                sx={{ ml: 0.5, color: 'text.secondary' }}
                              />
                            ) : assigneeType === 'user' ? (
                              <PersonIcon
                                fontSize='small'
                                sx={{ ml: 0.5, color: 'text.secondary' }}
                              />
                            ) : null}
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <ListItem {...props} key={`${option.type}-${option.id}`}>
                      <ListItemText
                        primary={option.name}
                        secondary={
                          option.type === 'Gruppe' ? 'Gruppe' : 'Mitarbeiter'
                        }
                      />
                    </ListItem>
                  )}
                />
              </Stack>
              <Stack direction='row' spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={stateId}
                    onChange={e => setStateId(Number(e.target.value) || '')}
                    label='Status'
                  >
                    {(legalTransactionStates || []).map(s => (
                      <MenuItem
                        key={s.idLegalTransactionState}
                        value={s.idLegalTransactionState}
                      >
                        {s.LegalTransactionState}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Phase</InputLabel>
                  <Select
                    value={progressId}
                    onChange={e => setProgressId(Number(e.target.value) || '')}
                    label='Phase'
                  >
                    {(legalTransactionProgresses || []).map(p => (
                      <MenuItem
                        key={p.idLegalTransactionProgress}
                        value={p.idLegalTransactionProgress}
                      >
                        {p.LegalTransactionProgress}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Typography variant='subtitle2' sx={{ mb: 1 }}>
              Inhalte übernehmen
            </Typography>

            <Stack spacing={1}>
              {sections.map(section => (
                <SelectableSection
                  key={section.key}
                  section={section}
                  selected={selected[section.key]}
                  onChange={next =>
                    setSelected(prev => ({ ...prev, [section.key]: next }))
                  }
                />
              ))}
            </Stack>

            <Divider sx={{ my: 2 }} />

            <FormControl>
              <FormLabel>Abwicklung</FormLabel>
              <RadioGroup
                value={workflowMode}
                onChange={e => setWorkflowMode(e.target.value as WorkflowMode)}
              >
                <FormControlLabel
                  value='reset'
                  control={<Radio />}
                  label='Alle Maßnahmen / Vollzüge auf „Offen" zurücksetzen'
                />
                <FormControlLabel
                  value='keep'
                  control={<Radio />}
                  label='Aktueller Vollzugsstand beibehalten'
                />
              </RadioGroup>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={isPending}>
              Abbruch
            </Button>
            <Button
              variant='contained'
              onClick={handleCopy}
              disabled={isPending}
            >
              {isPending ? (
                <Box display='flex' alignItems='center' gap={1}>
                  <span>Kopiere…</span>
                  <CircularProgress size={18} />
                </Box>
              ) : (
                'Vorgang kopieren'
              )}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// SelectableSection — wiederverwendbarer Tree-Branch
// ---------------------------------------------------------------------------

interface ISelectableProps {
  section: ISectionDef;
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
}

// ---------------------------------------------------------------------------
// NumberListField — Mehrfach-Eingabe für Vorgangs-/Urkundennummern.
// Mit + / − pro Zeile, Pattern wie in `LegalTransactionEdit`.
// ---------------------------------------------------------------------------

interface INumberListFieldProps {
  label: string;
  values: string[];
  onChange: (next: string[]) => void;
}

const NumberListField: React.FC<INumberListFieldProps> = ({
  label,
  values,
  onChange,
}) => {
  const setAt = (idx: number, value: string) => {
    onChange(values.map((v, i) => (i === idx ? value : v)));
  };
  const removeAt = (idx: number) => {
    if (values.length <= 1) return;
    onChange(values.filter((_, i) => i !== idx));
  };
  const addRow = () => {
    if ((values[values.length - 1] || '').trim() === '') return;
    onChange([...values, '']);
  };
  return (
    <Stack spacing={1}>
      {values.map((v, idx) => (
        <Stack key={idx} direction='row' spacing={1} alignItems='center'>
          <TextField
            label={values.length > 1 ? `${label} ${idx + 1}` : label}
            value={v}
            onChange={e => setAt(idx, e.target.value)}
            fullWidth
            size='small'
          />
          <IconButton
            size='small'
            onClick={() => removeAt(idx)}
            disabled={values.length <= 1}
            aria-label={`${label} entfernen`}
          >
            <RemoveIcon fontSize='small' />
          </IconButton>
          {idx === values.length - 1 && (
            <IconButton
              size='small'
              onClick={addRow}
              disabled={!v.trim()}
              aria-label={`${label} hinzufügen`}
            >
              <AddIcon fontSize='small' />
            </IconButton>
          )}
        </Stack>
      ))}
    </Stack>
  );
};

const SelectableSection: React.FC<ISelectableProps> = ({
  section,
  selected,
  onChange,
}) => {
  const [expanded, setExpanded] = useState(false);
  const total = section.items.length;
  const selCount = selected.size;
  const allSelected = total > 0 && selCount === total;
  const someSelected = selCount > 0 && selCount < total;
  const empty = total === 0;

  const toggleAll = () => {
    if (allSelected || someSelected) {
      onChange(new Set());
    } else {
      onChange(new Set(section.items.map(i => i.id)));
    }
  };

  const toggleItem = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(next);
  };

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Stack direction='row' alignItems='center' sx={{ px: 1, py: 0.5 }}>
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected}
          onChange={toggleAll}
          disabled={empty}
        />
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant='body2' sx={{ fontWeight: 500 }}>
            {section.title}
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            {empty ? 'keine Einträge' : `${selCount} von ${total} ausgewählt`}
          </Typography>
        </Box>
        {!empty && (
          <IconButton size='small' onClick={() => setExpanded(p => !p)}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        )}
      </Stack>
      <Collapse in={expanded && !empty}>
        <Box sx={{ pl: 5, pr: 1, pb: 1 }}>
          {section.items.map(item => (
            <Stack
              key={item.id}
              direction='row'
              alignItems='flex-start'
              spacing={0.5}
              sx={{ py: 0.25 }}
            >
              <Checkbox
                size='small'
                checked={selected.has(item.id)}
                onChange={() => toggleItem(item.id)}
                sx={{ p: 0.5 }}
              />
              <Box>
                <Typography variant='body2'>{item.label}</Typography>
                {item.hint && (
                  <Typography variant='caption' color='text.secondary'>
                    {item.hint}
                  </Typography>
                )}
              </Box>
            </Stack>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Section-Builder (aus Original-FullObject die Items pro Bereich ableiten)
// ---------------------------------------------------------------------------

function buildSections(lt: ILegalTransactionFullObject): ISectionDef[] {
  // 1. Vertragsgegenstände — Grundstücke aus RelationshipArray.RealEstateArray
  const realEstateItems: ISectionItem[] = [];
  (lt.RelationshipArray || []).forEach((rel, relIdx) => {
    // RealEstateArray ist nicht im Type deklariert, daher per `any` einlesen.
    const reArr = ((rel as any).RealEstateArray || []) as any[];
    reArr.forEach((re, reIdx) => {
      const id = `re-${relIdx}-${reIdx}-${re.idRealEstate ?? re.RealEstate_idRealEstate ?? reIdx}`;
      const street = [re.Street, re.StreetNr].filter(Boolean).join(' ');
      const city = [re.Postcode, re.City].filter(Boolean).join(' ');
      const label =
        [street, city].filter(Boolean).join(', ') || 'Vertragsgegenstand';
      realEstateItems.push({
        id,
        label,
        hint: re.Description || re.AdministrativeUnit || undefined,
      });
    });
  });

  // 2. Datenerfassung — DataCollection-Einträge (CompanyFormation, Inherit, Mortgage)
  const dataCollectionItems: ISectionItem[] = (lt.DataCollection || []).map(
    (d, idx) => ({
      id: `dc-${idx}`,
      label: dataCollectionLabel(d, idx),
      hint: dataCollectionHint(d),
    })
  );

  // 3. Mandanten
  const clientItems: ISectionItem[] = (lt.LegalClientArray || []).map(c => {
    const isOrg = c.idLegalClientType === 2;
    const personName =
      [c.FirstName, c.LastName].filter(Boolean).join(' ').trim() ||
      'Unbekannter Beteiligter';
    const orgName = c.LastName?.trim() || 'Unbekannte Organisation';
    return {
      id: `lc-${c.idLegalClient}`,
      label: isOrg ? orgName : personName,
      hint: isOrg ? 'Juristische Person' : 'Natürliche Person',
    };
  });

  // 4. Entwürfe
  const draftItems: ISectionItem[] = (lt.DraftArray || []).map(d => ({
    id: `draft-${d.idDocument}`,
    label: d.FileName || `Entwurf #${d.idDocument}`,
  }));

  // 5. Öffentliche Dokumente
  const publicItems: ISectionItem[] = (lt.OtherDocumentArray || []).map(d => ({
    id: `pub-${d.idDocument}`,
    label: d.FileName || `Dokument #${d.idDocument}`,
  }));

  // 6. Interne Datenablage — DocumentArray + DocumentDirectoryArray
  const dirById: Record<number, string> = {};
  (lt.DocumentDirectoryArray || []).forEach(dd => {
    const idDir = (dd as any).idDocumentDirectory;
    if (idDir) dirById[idDir] = (dd as any).Name || (dd as any).Title || '';
  });
  const internalItems: ISectionItem[] = (lt.DocumentArray || []).map(
    (link, idx) => {
      const l = link as any;
      const dirName = dirById[l.DocumentDirectory_idDocumentDirectory] || '';
      const fileName = l.FileName || `Dokument #${l.idDocument || idx}`;
      return {
        id: `int-${l.idDocument ?? idx}-${idx}`,
        label: fileName,
        hint: dirName || undefined,
      };
    }
  );

  return [
    { key: 'realEstate', title: 'Vertragsgegenstände', items: realEstateItems },
    {
      key: 'dataCollection',
      title: 'Datenerfassung',
      items: dataCollectionItems,
    },
    { key: 'clients', title: 'Mandanten', items: clientItems },
    { key: 'drafts', title: 'Entwürfe', items: draftItems },
    { key: 'publicDocs', title: 'Öffentliche Dokumente', items: publicItems },
    { key: 'internalDocs', title: 'Interne Datenablage', items: internalItems },
  ];
}

function dataCollectionLabel(
  d: ILegalTransactionDataCollection,
  idx: number
): string {
  if ('idCompanyFormation' in d) {
    return (d as any).BusinessName || `Firmengründung #${idx + 1}`;
  }
  if ('idInherit' in d) {
    const a = d as any;
    return a.Title || a.Name || `Erbfall #${idx + 1}`;
  }
  if ('idMortgage' in d) {
    return (d as any).Title || `Grundschuld #${idx + 1}`;
  }
  return `Datensatz #${idx + 1}`;
}

function dataCollectionHint(
  d: ILegalTransactionDataCollection
): string | undefined {
  if ('idCompanyFormation' in d) return 'Firmengründung';
  if ('idInherit' in d) return 'Erbfall';
  if ('idMortgage' in d) return 'Grundschuld';
  return undefined;
}

// ---------------------------------------------------------------------------
// FullObject-Builder
// ---------------------------------------------------------------------------

interface IBuildArgs {
  original: ILegalTransactionFullObject;
  selected: Record<SectionKey, Set<string>>;
  workflowMode: WorkflowMode;
  header: {
    title: string;
    transactionNumbers: string[];
    recordNumbers: string[];
    notarizationDate: string | null;
    notaryId: number | null;
    employeeId: number | null;
    groupId: number | null;
    stateId: number;
    progressId: number;
  };
}

/** ISO-8601-Datumsstrings (``"2026-04-15T17:24:36Z"``) zu MariaDB-DATETIME
 *  (``"2026-04-15 17:24:36"``) umformen. Das FullObject vom Backend kommt
 *  oft im ISO-Format zurueck, das DB-INSERT-Statement akzeptiert das aber
 *  nicht und wirft ``Incorrect datetime value``. */
function _fixIsoDate(s: string): string {
  // Format-Erkennung: "YYYY-MM-DDTHH:MM:SS" (mit optionalem .ms / Z / TZ)
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}:\d{2})/.exec(s);
  if (!m) return s;
  return `${m[1]} ${m[2]}`;
}

function _normalizeDates<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map(_normalizeDates) as unknown as T;
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(value as Record<string, unknown>)) {
      const v = (value as Record<string, unknown>)[k];
      out[k] = typeof v === 'string' ? _fixIsoDate(v) : _normalizeDates(v);
    }
    return out as T;
  }
  return value;
}

function buildCopy({
  original,
  selected,
  workflowMode,
  header,
}: IBuildArgs): ILegalTransactionFullObject {
  const cloned: ILegalTransactionFullObject = _normalizeDates(
    JSON.parse(JSON.stringify(original)) as ILegalTransactionFullObject
  );

  // Top-Level-Boolean-Felder explizit casten — gleiches Pattern wie beim
  // regulaeren Save in `LegalTransactionEdit.createLegalTransactionSaveObject`.
  // MariaDB liefert TINYINT als 0/1, Connexion erwartet aber echte Booleans.
  cloned.UserInformed_LackOfActivity = Boolean(
    cloned.UserInformed_LackOfActivity
  );
  cloned.syncWithArnoTop = Boolean(cloned.syncWithArnoTop);
  if ('syncAllLegalClientsWithArnotop' in cloned) {
    cloned.syncAllLegalClientsWithArnotop = Boolean(
      cloned.syncAllLegalClientsWithArnotop
    );
  }

  // Created_at / Updated_at: gar nicht senden. Beide DB-Spalten haben
  // ``DEFAULT CURRENT_TIMESTAMP`` (bzw. ``ON UPDATE CURRENT_TIMESTAMP``) —
  // das DBMS setzt sie beim INSERT selbst, der Original-Wert vom Quell-
  // Vorgang würde nur stören und scheitert ausserdem am ISO-Format
  // (``2026-04-15T17:24:36Z``), das MariaDB-DATETIME nicht akzeptiert.
  delete (cloned as Partial<ILegalTransactionFullObject>).Created_at;
  delete (cloned as unknown as Record<string, unknown>).Updated_at;

  let idCounter = 0;
  const negId = () => --idCounter;

  // Header
  cloned.idLegalTransaction = negId();
  cloned.Title = header.title;
  cloned.Closed_at = null;
  cloned.LastActivity_at = null;
  cloned.LastPhaseUpdate_at = null;
  cloned.NotarizationDate = header.notarizationDate;
  cloned.StartEditDate = null;
  cloned.idLegalTransactionState = header.stateId;
  cloned.idLegalTransactionProgress = header.progressId;
  // Notary_idUser: das Schema verlangt einen number-Wert. -1 ist im DiNo-
  // Code die Konvention für "nicht zugewiesen".
  cloned.Notary_idUser = header.notaryId ?? -1;
  // Employee_idUser und idUserGroup schliessen sich gegenseitig aus.
  cloned.Employee_idUser = header.employeeId;
  (cloned as ILegalTransactionFullObject).idUserGroup = header.groupId;

  cloned.RecordNumberArray = header.recordNumbers.map(
    entry =>
      ({
        idLegalTransactionRecordNumber: negId(),
        RecordNumberEntry: entry,
      }) as unknown as ILegalTransactionFullObject['RecordNumberArray'][number]
  );
  cloned.TransactionNumberArray = header.transactionNumbers.map(
    entry =>
      ({
        idLegalTransactionTransactionNumber: negId(),
        TransactionNumberEntry: entry,
      }) as unknown as ILegalTransactionFullObject['TransactionNumberArray'][number]
  );

  // Was IMMER ausgeschlossen wird:
  cloned.CertificateArray = [];
  cloned.InvoiceArray = [];
  cloned.InvoiceObjectArray = [];
  cloned.NoteArray = [];
  cloned.Tasks = [];
  cloned.LogArray = [];
  cloned.LegalTransactionMeetingArray = [];
  cloned.LegalTransactionMeetingEmailArray = [];
  cloned.LegalTransactionTalkRoomArray = [];
  cloned.LegalTransactionGwGArray = [];
  cloned.LegalTransaction_has_LegalTransactionSpecialFunctionArray = [];

  // 1. Vertragsgegenstände — Grundstücke pro Relationship filtern.
  cloned.RelationshipArray = (cloned.RelationshipArray || []).map(
    (rel, relIdx) => {
      const r = rel as any;
      const reArr = (r.RealEstateArray || []) as Array<{
        idRealEstate?: number;
        RealEstate_idRealEstate?: number;
      }>;
      const filteredRe = reArr.filter((re, reIdx) => {
        const id = `re-${relIdx}-${reIdx}-${re.idRealEstate ?? re.RealEstate_idRealEstate ?? reIdx}`;
        return selected.realEstate.has(id);
      });
      return { ...rel, RealEstateArray: filteredRe } as typeof rel;
    }
  );

  // 2. Datenerfassung
  cloned.DataCollection = (cloned.DataCollection || []).filter((_, idx) =>
    selected.dataCollection.has(`dc-${idx}`)
  );

  // 3. Mandanten — wenn ein Mandant abgewählt ist, fällt er aus dem Array;
  //    Beziehungen, die nur diesen Mandanten kennen, bleiben drin (Backend
  //    räumt FK auf neuen Vorgang).
  const keptClientIds = new Set<number>();
  cloned.LegalClientArray = (cloned.LegalClientArray || []).filter(c => {
    if (selected.clients.has(`lc-${c.idLegalClient}`)) {
      keptClientIds.add(c.idLegalClient);
      return true;
    }
    return false;
  });
  // RelationshipArray-Einträge aufräumen, deren LegalClient abgewählt wurde.
  cloned.RelationshipArray = (cloned.RelationshipArray || []).filter(rel => {
    const r = rel as any;
    const cid = r.LegalClient_idLegalClient ?? r.idLegalClient;
    if (cid == null) return true;
    return keptClientIds.has(cid);
  });

  // 4. Entwürfe — IDs negativ, damit Backend neu anlegt.
  cloned.DraftArray = (cloned.DraftArray || [])
    .filter(d => selected.drafts.has(`draft-${d.idDocument}`))
    .map(d => ({ ...d, idDocument: negId() }));
  // DraftMessageArray hängt an DraftArray — behalten, wenn mind. ein Draft da ist.
  if (cloned.DraftArray.length === 0) {
    cloned.DraftMessageArray = [];
  }

  // 5. Öffentliche Dokumente
  cloned.OtherDocumentArray = (cloned.OtherDocumentArray || [])
    .filter(d => selected.publicDocs.has(`pub-${d.idDocument}`))
    .map(d => ({ ...d, idDocument: negId() }));

  // 6. Interne Datenablage — DocumentArray ausdünnen, DocumentDirectoryArray
  //    bleibt vorerst leer (komplexe Hierarchie wird vom Backend nicht
  //    verlustfrei aus der Frontend-Struktur rekonstruiert).
  cloned.DocumentArray = (cloned.DocumentArray || []).filter((link, idx) => {
    const l = link as any;
    const id = `int-${l.idDocument ?? idx}-${idx}`;
    return selected.internalDocs.has(id);
  });
  // DirectoryArray nur dann übernehmen, wenn überhaupt Dokumente kopiert werden.
  if (cloned.DocumentArray.length === 0) {
    cloned.DocumentDirectoryArray = [];
  }

  // Abwicklung
  if (workflowMode === 'reset') {
    cloned.LegalPhaseArray = (cloned.LegalPhaseArray || []).map(phase => ({
      ...phase,
      LegalRequirementArray: (phase.LegalRequirementArray || []).map(req => {
        const reset = { ...req, idLegalRequirementState: STATE_OFFEN };
        delete reset.DeadlineFullfilledAt;
        delete reset.actualProcessingTime;
        return reset;
      }),
    }));
    cloned.ProcessManagerArray = [];
  }

  return cloned;
}

// ---------------------------------------------------------------------------
// mergePhaseStatesByPosition
// ---------------------------------------------------------------------------
// Das Backend generiert die LegalPhasen + Requirements beim POST aus dem
// LegalTransactionType-Template (immer mit Default-State "Offen" / id=10).
// Wenn der User "Aktueller Vollzugsstand beibehalten" gewaehlt hat, muessen
// wir die States des Original-Vorgangs auf die frisch erzeugten Phasen
// uebertragen — pro Phase per ``Position``, pro Requirement per Index in
// der LegalRequirementArray (alphabetisch durch Backend-Generierung).
// ---------------------------------------------------------------------------

type Phase = ILegalTransactionFullObject['LegalPhaseArray'][number];

function mergePhaseStatesByPosition(target: Phase[], source: Phase[]): Phase[] {
  if (!source || source.length === 0) return target;
  const srcByPos = new Map<number, Phase>();
  source.forEach((p, i) =>
    srcByPos.set(typeof p.Position === 'number' ? p.Position : i, p)
  );

  return target.map((targetPhase, idx) => {
    const key =
      typeof targetPhase.Position === 'number' ? targetPhase.Position : idx;
    const srcPhase = srcByPos.get(key);
    if (!srcPhase) return targetPhase;

    const srcReqs = srcPhase.LegalRequirementArray || [];
    const mergedReqs = (targetPhase.LegalRequirementArray || []).map(
      (req, rIdx) => {
        const srcReq = srcReqs[rIdx];
        if (!srcReq) return req;
        return {
          ...req,
          idLegalRequirementState: srcReq.idLegalRequirementState,
          isComplete: srcReq.isComplete,
          DeadlineFullfilledAt: srcReq.DeadlineFullfilledAt,
          actualProcessingTime: srcReq.actualProcessingTime,
          hasError: srcReq.hasError,
          TextOfError: srcReq.TextOfError,
        };
      }
    );

    return {
      ...targetPhase,
      isComplete: srcPhase.isComplete,
      LegalRequirementArray: mergedReqs,
    };
  });
}
